// import React from 'react';
// import { services } from '../data/services';
// import { motion } from 'framer-motion';

// const ServicesGrid = () => {
//   return (
//     <div className="max-w-7xl mx-auto px-6 py-16">

//       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
//         {services.map((service, index) => (
//           <motion.div
//             key={service.id}
//             initial={{ opacity: 0, y: 30 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             whileHover={{ scale: 1.05 }}
//             transition={{ duration: 0.5, delay: index * 0.05 }}
//             className="relative bg-white overflow-hidden rounded-xl shadow-md cursor-pointer group"
//           >
//             <div className="h-64 w-full overflow-hidden relative">
//               <motion.img
//                 src={service.image}
//                 alt={service.title}
//                 className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
//               />
//               <motion.div
//                 className="absolute inset-0 bg-darkBlue/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
//               />
//               <motion.div
//                 className="absolute bottom-0 left-0 right-0 p-5 translate-y-full group-hover:translate-y-0 transition-transform duration-500 z-20"
//               >
//                 <div className="flex items-center gap-3 mb-1">
//                   <span className="text-3xl text-white">{service.icon}</span>
//                   <h3 className="text-xl font-bold text-white">{service.title}</h3>
//                 </div>
//                 <p className="text-sm text-white/90">{service.shortDesc}</p>
//               </motion.div>
//             </div>
//           </motion.div>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default ServicesGrid;


import React from 'react';
import { useNavigate } from 'react-router-dom';
import { services } from '../data/services';
import { motion } from 'framer-motion';

interface ServicesGridProps {
  imageHeightClass?: string; // e.g., "h-64" or "h-96"
}

const ServicesGrid: React.FC<ServicesGridProps> = ({ imageHeightClass = "h-64" }) => {
  // Brand colors array for cycling through cards
  const brandColors = [
    'masa-coral',
    'masa-yellow', 
    'masa-teal',
    'masa-blue',
    'masa-magenta'
  ];

  // Gradient combinations using brand colors
  const brandGradients = [
    'from-masa-coral to-masa-magenta',
    'from-masa-yellow to-masa-coral',
    'from-masa-teal to-masa-blue',
    'from-masa-blue to-masa-teal',
    'from-masa-magenta to-masa-coral'
  ];
  const navigate = useNavigate();
  
  // ... existing brand colors and gradients ...

  const handleServiceClick = (serviceId: number) => {
    navigate(`/services/${serviceId}`);
  };

  const isLightGradient = (colorIndex: number) => {
    // Adjust this logic based on your actual color palette
    // Here, index 1 is the yellow gradient, which is light
    return colorIndex === 1;
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {services.map((service, index) => {
          const colorIndex = index % brandColors.length;
          const gradientClass = brandGradients[colorIndex];
          const accentColor = brandColors[colorIndex];
          
          return (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="relative bg-white overflow-hidden rounded-xl shadow-lg hover:shadow-2xl cursor-pointer group border-2 border-gray-100 hover:border-masa-blue/20 transition-all duration-300"
            >
              {/* Brand accent top border */}
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradientClass}`}></div>
              
              <div className={`${imageHeightClass} w-full overflow-hidden relative`}>
                <motion.img
                  src={service.image}
                  alt={`MASA HILLS Health Services - ${service.title}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                
                {/* Brand color overlay on hover */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-br ${gradientClass} opacity-0 group-hover:opacity-90 transition-all duration-300 z-10`}
                />
                
                {/* Service info overlay - Only visible on hover */}
                <motion.div
                  className={`absolute inset-0 p-6 opacity-0 group-hover:opacity-100 transition-all duration-300 z-20`}
                >
                  <div className="flex items-start gap-4 mb-3">
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 border border-white/30">
                      <span className={`text-2xl ${isLightGradient(colorIndex) ? 'text-masa-dark' : 'text-white'}`}>{service.icon}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-xl font-bold mb-2 ${isLightGradient(colorIndex) ? 'text-masa-dark' : 'text-white'}`}>{service.title}</h3>
                      <p className={`text-sm leading-relaxed ${isLightGradient(colorIndex) ? 'text-masa-dark/80' : 'text-white/95'}`}>{service.shortDesc}</p>
                    </div>
                  </div>
                  
                  {/* Brand accent line */}
                  <div className="w-full h-0.5 bg-white/30 rounded-full mb-3"></div>
                  
                  {/* CTA button with brand styling */}
                  <button 
                    onClick={() => handleServiceClick(service.id)}
                    className={`w-full bg-white/20 backdrop-blur-sm font-semibold py-3 px-4 rounded-lg border border-white/30 hover:bg-white/30 transition-all duration-200 text-sm ${isLightGradient(colorIndex) ? 'text-masa-dark' : 'text-white'}`}
                  >
                    Learn More About This Service
                  </button>
                </motion.div>

              
              </div>

              {/* Bottom section with brand styling */}
              <div className="p-6 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{service.icon}</span>
                    <span className="text-sm font-medium text-masa-dark">{service.title}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4 text-masa-yellow fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                      </svg>
                    ))}
                  </div>
                </div>
                
                {/* Service preview text */}
                <p className="text-gray-600 text-sm mt-3 leading-relaxed">
                  {service.shortDesc}
                </p>
                
                {/* Brand accent bottom border */}
                <div className={`w-full h-0.5 bg-gradient-to-r ${gradientClass} mt-4 rounded-full opacity-30`}></div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Call to Action Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mt-16 text-center"
      >
        <div className="bg-gradient-to-r from-masa-blue/5 to-masa-teal/5 rounded-2xl p-8 border border-masa-blue/10">
          <h3 className="text-2xl font-bold text-masa-dark mb-4">
            Need Help Choosing the Right Service?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Our experienced team at MASA HILLS Health Services is here to help you find the perfect care solution for your needs.
          </p>
          
          {/* Brand color accent dots */}
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="w-3 h-3 bg-masa-coral rounded-full"></div>
            <div className="w-3 h-3 bg-masa-yellow rounded-full"></div>
            <div className="w-3 h-3 bg-masa-teal rounded-full"></div>
            <div className="w-3 h-3 bg-masa-blue rounded-full"></div>
            <div className="w-3 h-3 bg-masa-magenta rounded-full"></div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="tel:+14695730176"
              className="px-8 py-3 bg-gradient-to-r from-masa-coral to-masa-magenta text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 transform duration-300"
            >
              Call for Consultation
            </a>
            <button
              className="px-8 py-3 border-2 border-masa-blue text-masa-blue font-semibold rounded-lg hover:bg-masa-blue hover:text-white transition-all hover:scale-105 transform duration-300"
              onClick={() => navigate('/contact')}
            >
              Schedule Assessment
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ServicesGrid;